// ══════════════════════════════════════════════════════════════════════════════
//  #UNIT_VALID_V1 — Fleet's data-hygiene gate, the twin of ducValid.js.
//
//  STANDING RULE (Bill, 2026-08-17):  Unit # = YEAR + LAST 4 OF VIN   →  "NN-XXXX"
//
//  This is self-checking: a unit number can be validated against its OWN vin with no
//  lookup table. Verified against the 344-row master — 311 clean, 6 genuine conflicts,
//  the rest legitimately have no VIN (shop-made reels, DDD tanks, the gyro module).
//
//  Why this file exists: the master's daily rate (ducDay) is joined to Daily, INV.,
//  INSPEC, TOLL and the PO-system COST extract purely by unit number. Every one of those
//  joins is a silent VLOOKUP today, and the COST extract alone carried 8 single-digit
//  transposition typos (01-0890 for 01-8090, 14-0521 for 14-0251, …). A wrong unit number
//  doesn't error — it just puts cost on the wrong machine, forever.
// ══════════════════════════════════════════════════════════════════════════════

// Canonical display form. Trailing '*' is a master convention (e.g. "20-0213*") and is dropped.
export const unitNorm = (u) =>
  String(u == null ? '' : u).trim().toUpperCase().replace(/\s+/g, '').replace(/\*+$/, '');

// Comparison key — strips the hyphen so "16-5230" and "165230" (the TOLL sheet's second
// column) resolve to the same unit.
export const unitKey = (u) => unitNorm(u).replace(/[^0-9A-Z]/g, '');

// NN-XXXX. Tail may be alphanumeric: the master legitimately holds 18-F523, 19-K908, 22-J038.
export const unitShape = (u) => /^\d{2}-[0-9A-Z]{4}$/.test(unitNorm(u));

// #UNIT_LEGACY_V1 — Bill, 2026-08-17: "One goofy part are these two excavators use YM (Old system
// but has all data tied to them)". YM10001 / YM3501 are the hammer excavators, carried over from the
// pre-2020 numbering with years of cost and hours attached. Renumbering them would orphan that
// history, so the shape check has to KNOW about them rather than nag about them forever.
// Letters + digits, no hyphen. These are legitimate, not errors — and they have no VIN rule.
export const unitLegacy = (u) => /^[A-Z]{2}\d{3,6}$/.test(unitNorm(u));

// #FLEET_RENTAL_V1 — Bill, 2026-08-21: "Rental units will follow our format but have a R at the end
// XX-XXXXR". Same NN-XXXX shape as an owned unit, plus a trailing R so a rental/RPO record reads
// apart from owned equipment on sight.
// #FLEET_RENTAL_V3 — Bill, 2026-09-08: flipped the marker to the FRONT — "R" + NN-XXXX (e.g.
// "R27-4966") — to ease sorting/visual grouping (all rentals now sort together instead of falling
// in among owned units with the same year prefix). Old suffix-form records (e.g. "27-4966R") are
// still recognized so nothing already on file flags as invalid while it gets renamed.
export const unitRentalShape = (u) => {
  const n = unitNorm(u);
  return /^R\d{2}-[0-9A-Z]{4}$/.test(n) || /^\d{2}-[0-9A-Z]{4}R$/.test(n);
};
export const unitOk = (u) => unitShape(u) || unitLegacy(u) || unitRentalShape(u);
// Strip the rental marker (leading or trailing R) so year/tail extraction below works the same for
// a rental unit as it does for an owned one — only applied when unitRentalShape already says this
// IS a rental, so a legitimate owned-unit tail that happens to end in "R" is never touched.
const rentalCore = (n, u) => unitRentalShape(u) ? n.replace(/^R/, '').replace(/R$/, '') : n;

// A VIN/serial cell that holds a note rather than a VIN. 21 master rows look like this.
export const isVinPlaceholder = (v) => {
  const s = String(v == null ? '' : v).trim();
  if (!s) return true;
  return /^(n\/?a|none|0|00:00:00)$/i.test(s) || /added|manual|no vin|\?\?/i.test(s);
};

export const vinClean = (v) => String(v == null ? '' : v).toUpperCase().replace(/[^0-9A-Z]/g, '');
export const vinTail = (v) => { const s = vinClean(v); return s.length >= 4 ? s.slice(-4) : ''; };
export const unitTail = (u) => { const n = rentalCore(unitNorm(u), u); const m = n.match(/^\d{2}-([0-9A-Z]{4})$/); return m ? m[1] : ''; };
export const unitYear = (u) => { const n = rentalCore(unitNorm(u), u); const m = n.match(/^(\d{2})-/); return m ? m[1] : ''; };

// ── the rule check ───────────────────────────────────────────────────────────
// Levels:
//   ok      — tail matches the end of the VIN. 311 of 344 master rows.
//   info    — no VIN to check against (shop-made, tank, module). Not an error.
//   warn    — the tail IS in the VIN but not at the end. Serial has a prefix/suffix, or the
//             cell holds two VINs ("trailer / vac"). Cosmetic, not a numbering fault.
//   bad     — tail is nowhere in the VIN. The unit number and the machine disagree.
export const unitVsVin = (unit, vin) => {
  const tail = unitTail(unit);
  if (unitLegacy(unit)) return { level: 'info', msg: 'Legacy unit number (pre-VIN scheme) — history is tied to it' };
  // #FLEET_RENTAL_V1 — a rental/RPO unit is leased, not bought: there is no VIN rule to check it
  // against, and Bill's numbers are placeholders until the real ones arrive. Not an error.
  if (unitRentalShape(unit)) return { level: 'info', msg: 'Rental/RPO unit — number may be a placeholder until the real one is set' };
  if (!unitShape(unit)) return { level: 'warn', msg: 'Unit is not in NN-XXXX form' };
  if (isVinPlaceholder(vin)) return { level: 'info', msg: 'No VIN on file' };
  const v = vinClean(vin);
  if (!tail) return { level: 'warn', msg: 'Cannot read unit tail' };
  if (v.endsWith(tail)) return { level: 'ok', msg: 'Matches last 4 of VIN' };
  if (v.includes(tail)) return { level: 'warn', msg: `Tail "${tail}" is in the VIN but not at the end` };
  return { level: 'bad', msg: `VIN ends "${vinTail(vin)}" but unit says "${tail}"` };
};

// What the unit number SHOULD be, given a VIN and a year. Used by the add form so a new
// unit is derived rather than typed.
export const unitFromVin = (vin, year) => {
  const t = vinTail(vin);
  const y = String(year == null ? '' : year).trim();
  const yy = /^\d{4}$/.test(y) ? y.slice(2) : (/^\d{2}$/.test(y) ? y : '');
  return (yy && t) ? `${yy}-${t}` : '';
};

// ── typo rescue ──────────────────────────────────────────────────────────────
// A single adjacent-digit swap, or a dropped/added leading zero. This is exactly how the
// PO-system COST extract drifts, and it is why an unmatched unit should SUGGEST rather
// than silently fail the way the spreadsheet VLOOKUP does.
export const transposeCandidates = (unit, pool) => {
  const n = unitKey(unit);
  if (!n) return [];
  const hit = new Set();
  for (let i = 0; i < n.length - 1; i++) {
    const s = n.split('');
    [s[i], s[i + 1]] = [s[i + 1], s[i]];
    const c = s.join('');
    if (c !== n && pool.has(c)) hit.add(c);
  }
  const stripped = n.replace(/^0+/, '');
  if (stripped && stripped !== n && pool.has(stripped)) hit.add(stripped);
  for (const z of ['0' + n, '00' + n]) if (pool.has(z)) hit.add(z);
  return [...hit];
};

// Pretty-print a key back to NN-XXXX for display in a suggestion.
export const keyToDisplay = (k) => {
  const s = String(k || '').toUpperCase();
  return s.length === 6 ? `${s.slice(0, 2)}-${s.slice(2)}` : s;
};
